import { useState, useRef, useEffect } from "react";
import { FaEnvelope, FaCheck, FaTimes, FaBuilding, FaUsers } from "react-icons/fa";
import { LuLoader } from "react-icons/lu";
import { BiCross } from "react-icons/bi";
import { CgClose } from "react-icons/cg";
import { useInvitations, useRespondToInvitation } from "../../hooks/useInvitationQuery";

const InvitationDropdown = ({ isOpen, setIsOpen }) => {
    const [respondingTo, setRespondingTo] = useState(null);
    const dropdownRef = useRef();
    const showInvitations = isOpen;
    const setShowInvitations = setIsOpen;

    // Use the new hooks
    const { data: invitations = [], isLoading: loading, refetch } = useInvitations();
    const respondToInvitationMutation = useRespondToInvitation();

    // Respond to invitation (accept or decline)
    const respondToInvitation = async (invitationId, accept) => {
        try {
            setRespondingTo(invitationId);
            await respondToInvitationMutation.mutateAsync({ invitationId, accept });
        } catch (error) {
            console.error('Error responding to invitation:', error);
        } finally {
            setRespondingTo(null);
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowInvitations(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Refetch invitations when dropdown opens (optional, since data is already auto-updating)
    useEffect(() => {
        if (showInvitations) {
            refetch();
        }
    }, [showInvitations, refetch]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    const pendingInvitations = invitations.filter(inv => inv.status === 'Pending');

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Enhanced Dropdown */}
            {showInvitations && (
                    <><div className="absolute top-3.5 right-3 md:right-6  w-3.5  h-3.5 bg-primary_hover transform -rotate-45  border-r border-b border-primary z-80"></div>
                    <div className="absolute right-[1px] md:right-2 mt-5 w-64 sm:w-72 md:w-80 lg:w-96 bg-white rounded-lg shadow-2xl z-50 overflow-hidden">

                    <div className="bg-gradient-to-r from-primary to-primary_hover px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8  bg-white/20 rounded-full flex items-center justify-center ">
                                    <FaUsers className="text-white text-sm" />
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-white">Invitations</h3>
                                    <p className="text-blue-100 text-xs">
                                        {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3"></div>
                                <p className="text-gray-500 text-xs sm:text-base">Loading invitations...</p>
                            </div>
                        ) : pendingInvitations.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <FaEnvelope className="text-gray-400 text-xl" />
                                </div>
                                <h4 className="text-gray-600 font-medium text-xs sm:text-base mb-2">No pending invitations</h4>
                                <p className="text-gray-400 text-xs sm:text-sm text-center px-6">
                                    You're all caught up! New invitations will appear here.
                                </p>
                            </div>
                        ) : (
                            <div className="p-4 space-y-3">
                                {pendingInvitations.map((invitation, index) => (
                                    <div
                                        key={invitation.id}
                                        className="group bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 border border-gray-200 hover:border-blue-300 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                                        style={{
                                            animationDelay: `${index * 100}ms`
                                        }}
                                    >
                                        <div className="flex items-start gap-4">
                                            {/* Modern Avatar */}
                                            <div className="relative">
                                                <div className="md:w-12 md:h-12 w-8 h-8 bg-gradient-to-r from-primary to-primary_hover rounded-xl flex items-center justify-center text-white shadow-lg">
                                                    <FaBuilding className="text-lg" />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-xs sm:text-base truncate">
                                                            {invitation.centerName}
                                                        </h4>
                                                        <p className="text-xs text-gray-500">
                                                            Invited by {invitation.senderName || 'Admin'}
                                                        </p>
                                                    </div>
                                                    <span className="md:px-2 md:py-1 p-1 bg-yellow-100 text-yellow-800 text-[10px] md:text-xs rounded-full font-medium whitespace-nowrap">
                                                        Pending
                                                    </span>
                                                </div>

                                                <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-3">
                                                    You've been invited to join <b>{invitation.centerName}  </b> as a team member.
                                                </p>

                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] md:text-xs text-gray-400">
                                                        {formatDate(invitation.sentDate)}
                                                    </span>

                                                    {/* Modern Action Buttons */}
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => respondToInvitation(invitation.id, false)}
                                                            disabled={respondingTo === invitation.id || respondToInvitationMutation.isPending}
                                                            className="px-2 py-2 text-sm  font-medium rounded-full border border-red-500 hover:bg-red-400 text-red-400  hover:text-white rounded-full  disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {respondingTo === invitation.id ? <LuLoader className="animate-spin text-white" /> : <CgClose className="text-sm  " />}
                                                        </button>
                                                        <button
                                                            onClick={() => respondToInvitation(invitation.id, true)}
                                                            disabled={respondingTo === invitation.id || respondToInvitationMutation.isPending}
                                                            className="px-2 py-2 text-sm font-medium  border-green-500 border rounded-full hover:bg-green-400 text-green-500 hover:text-white transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-sm"
                                                        >
                                                            {respondingTo === invitation.id ? <LuLoader className="animate-spin text-white" /> : <FaCheck className="" />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>


                </div></>
            )}
        </div>
    );
};

export default InvitationDropdown;